/**
 * Data Service Router
 * Issue #236: LocalStorage → Supabase 段階的移行対応
 * 
 * 環境変数によってLocalStorageまたはSupabaseを選択的に使用するサービス
 * 段階的移行・ロールバック・A/Bテスト対応
 */

import * as LocalStorageService from '../utils/localStorage';
import * as SupabaseApiService from './supabaseApi';
import * as NestJSApiService from './nestjsApiService';
import { getSupabaseAvailability } from '../lib/supabase';
import logger from '../utils/logger';
import type { Customer, TreatmentRecord, Sequences } from '../utils/localStorage';
import type { ExtendedReservation } from '../types/salonboard';
import type { StaffShiftData, ExtendedStaffShiftData } from '../types/extended-shift';
import type { TreatmentRecordInput } from '../types/treatment';

// 型の再エクスポート
export type { Customer, TreatmentRecord, Sequences } from '../utils/localStorage';
export type { ExtendedReservation } from '../types/salonboard';
export type { StaffShiftData, ExtendedStaffShiftData } from '../types/extended-shift';
export type { TreatmentRecordInput } from '../types/treatment';

// 環境設定タイプ
export type DataProvider = 'localStorage' | 'supabase' | 'nestjs' | 'hybrid';

// 設定インターフェース
export interface DataServiceConfig {
  provider: DataProvider;
  fallbackProvider?: DataProvider;
  hybridConfig?: {
    customers: 'localStorage' | 'supabase' | 'nestjs';
    reservations: 'localStorage' | 'supabase' | 'nestjs';
    treatments: 'localStorage' | 'supabase' | 'nestjs';
    sequences: 'localStorage' | 'supabase' | 'nestjs';
    staffShifts: 'localStorage' | 'supabase' | 'nestjs';
    treatmentRecords: 'localStorage' | 'supabase' | 'nestjs';
    settings: 'localStorage' | 'supabase' | 'nestjs'; // Issue #238: Settings永続化
  };
}

// データ移行関連の型定義
export interface MigrationBackup {
  customers?: Customer[];
  reservations?: ExtendedReservation[];
  treatments?: TreatmentRecord[];
  sequences?: Sequences;
}

export interface BackupData {
  timestamp: string;
  localStorageData: {
    customers: Customer[];
    reservations: ExtendedReservation[];
    treatments: TreatmentRecord[];
  };
  supabaseData: {
    customers: Customer[];
    reservations: ExtendedReservation[];
    treatments: TreatmentRecord[];
  } | null;
}

export interface MigrationResult {
  success: boolean;
  migratedCounts: {
    customers: number;
    reservations: number;
    treatments: number;
  };
  errors: string[];
  backup?: BackupData;
}

// Settings関連の型定義
export interface StoreSettings {
  storeName?: string;
  taxRate?: number;
  [key: string]: unknown;
}

export interface PasswordPolicySettings {
  minLength?: number;
  expirationDays?: number;
  [key: string]: unknown;
}

export interface UserSettings {
  passwordPolicy?: PasswordPolicySettings;
  [key: string]: unknown;
}

export interface DataSettings {
  customerData?: {
    retentionDays?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface SettingsData {
  [key: string]: unknown; // index signature for dynamic access
  store?: StoreSettings;
  users?: UserSettings;
  data?: DataSettings;
  notifications?: Record<string, unknown>;
  ai?: Record<string, unknown>;
  display?: Record<string, unknown>;
  system?: Record<string, unknown>;
  lastUpdated?: string;
}

export interface SettingsResponse {
  success: boolean;
  data: SettingsData | null;
  error: string | null;
}

export interface SettingsBackup {
  settings: SettingsData;
  timestamp: string;
  version: string;
}

/**
 * データサービス統合管理クラス
 */
export class DataService {
  
  private static config: DataServiceConfig;
  private static isInitialized = false;

  /**
   * サービス初期化
   */
  static initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // 環境変数から設定を決定
    const provider = this.determineProvider();

    this.config = {
      provider,
      fallbackProvider: (provider === 'supabase' || provider === 'nestjs') ? 'localStorage' : undefined,
      hybridConfig: {
        customers: provider === 'hybrid' ? 'localStorage' : provider,
        reservations: provider === 'hybrid' ? 'localStorage' : provider,
        treatments: provider === 'hybrid' ? 'localStorage' : provider,
        sequences: provider === 'hybrid' ? 'localStorage' : provider,
        staffShifts: provider === 'hybrid' ? 'localStorage' : provider,
        treatmentRecords: provider === 'hybrid' ? 'localStorage' : provider,
        settings: provider === 'hybrid' ? 'localStorage' : provider // Issue #238: Settings永続化
      }
    };

    logger.log(`🔧 DataService initialized with provider: ${provider}`);
    this.isInitialized = true;
  }

  /**
   * DataServiceを強制的に再初期化
   * Supabase利用可能状態の変化後に使用
   */
  static reinitialize(): void {
    logger.log('🔄 DataService reinitializing...');
    this.isInitialized = false;
    this.initialize();
  }

  /**
   * LocalStorageからSupabaseへの安全なデータ移行
   * Phase 1.3: データ移行安全機能
   */
  static async migrateFromLocalStorageToSupabase(): Promise<MigrationResult> {
    const migrationResult: MigrationResult = {
      success: false,
      migratedCounts: { customers: 0, reservations: 0, treatments: 0 },
      errors: [] as string[],
      backup: undefined
    };

    try {
      logger.log('🚀 Starting safe data migration: LocalStorage → Supabase');

      // 1. 事前チェック: Supabaseが利用可能か確認
      if (!getSupabaseAvailability()) {
        migrationResult.errors.push('Supabase not available - cannot proceed with migration');
        return migrationResult;
      }

      // 2. 移行前バックアップ作成
      logger.log('📦 Creating backup before migration...');
      const backup = await DataService.createDataBackup();
      migrationResult.backup = backup;

      // 3. LocalStorageデータ読み込み（fallbackを無効にして直接読み込み）
      logger.log('📖 Reading data from LocalStorage...');
      const localStorageData = await DataService.readLocalStorageData();

      if (!localStorageData.customers.length && !localStorageData.reservations.length && !localStorageData.treatments.length) {
        logger.log('ℹ️ No data found in LocalStorage - migration skipped');
        migrationResult.success = true;
        return migrationResult;
      }

      // 4. Supabaseへデータ移行
      logger.log('☁️ Migrating data to Supabase...');
      const migrationResults = await Promise.allSettled([
        DataService.migrateCustomersToSupabase(localStorageData.customers),
        DataService.migrateReservationsToSupabase(localStorageData.reservations),
        DataService.migrateTreatmentsToSupabase(localStorageData.treatments)
      ]);

      // 5. 移行結果の処理
      migrationResults.forEach((result, index) => {
        const entityTypes = ['customers', 'reservations', 'treatments'] as const;
        const entityType = entityTypes[index];

        if (result.status === 'fulfilled') {
          migrationResult.migratedCounts[entityType] = result.value;
          logger.log(`✅ Migrated ${result.value} ${entityType}`);
        } else {
          migrationResult.errors.push(`Failed to migrate ${entityType}: ${result.reason}`);
          logger.error(`❌ Migration failed for ${entityType}:`, result.reason);
        }
      });

      // 6. データ整合性チェック
      logger.log('🔍 Verifying data integrity...');
      const integrityCheck = await DataService.verifyMigrationIntegrity(localStorageData);
      
      if (!integrityCheck.isValid) {
        migrationResult.errors.push(...integrityCheck.errors);
        throw new Error(`Data integrity check failed: ${integrityCheck.errors.join(', ')}`);
      }

      // 7. 成功判定
      const hasErrors = migrationResult.errors.length > 0;
      migrationResult.success = !hasErrors;

      if (migrationResult.success) {
        logger.log('🎉 Data migration completed successfully!');
        
        // プロバイダーをSupabaseに更新
        DataService.reinitialize();
      } else {
        logger.error('❌ Data migration completed with errors:', migrationResult.errors);
      }

      return migrationResult;

    } catch (error) {
      logger.error('💥 Critical migration error:', error);
      migrationResult.errors.push(`Critical error: ${(error as Error).message}`);

      // エラー時はバックアップからの復旧を試行
      if (migrationResult.backup) {
        try {
          logger.log('🔄 Attempting to restore from backup...');
          await DataService.restoreFromBackup(migrationResult.backup);
          logger.log('✅ Backup restoration completed');
        } catch (restoreError) {
          logger.error('❌ Backup restoration failed:', restoreError);
          migrationResult.errors.push(`Backup restoration failed: ${(restoreError as Error).message}`);
        }
      }

      return migrationResult;
    }
  }

  /**
   * データバックアップ作成
   * 移行前の安全措置として現在のデータを保存
   */
  private static async createDataBackup(): Promise<BackupData> {
    const timestamp = new Date().toISOString();
    
    try {
      // LocalStorageデータのバックアップ
      const localStorageData = await DataService.readLocalStorageData();
      
      // Supabaseデータのバックアップ（利用可能な場合）
      let supabaseData = null;
      if (getSupabaseAvailability()) {
        try {
          supabaseData = {
            customers: await SupabaseApiService.getCustomers(),
            reservations: await SupabaseApiService.getReservations(),
            treatments: await SupabaseApiService.getTreatments()
          };
        } catch (error) {
          logger.warn('⚠️ Failed to backup Supabase data:', error);
          supabaseData = { customers: [], reservations: [], treatments: [] };
        }
      }

      const backup = {
        timestamp,
        localStorageData,
        supabaseData
      };

      logger.log(`📦 Backup created at ${timestamp}`);
      return backup;
    } catch (error) {
      logger.error('❌ Failed to create backup:', error);
      throw error;
    }
  }

  /**
   * LocalStorageから直接データを読み込み
   * fallback機能を使わずに純粋なLocalStorageデータを取得
   */
  private static async readLocalStorageData(): Promise<{
    customers: Customer[];
    reservations: ExtendedReservation[];
    treatments: TreatmentRecord[];
  }> {
    try {
      // Node.js環境では空データを返す
      if (typeof window === 'undefined') {
        logger.log('ℹ️ Node.js environment - returning empty LocalStorage data');
        return { customers: [], reservations: [], treatments: [] };
      }

      const localStorageService = await import('../utils/localStorage.js');
      
      const customers = await localStorageService.getCustomers();
      const reservations = await localStorageService.getReservations();
      const treatments = await localStorageService.getTreatments();

      logger.log(`📖 Read from LocalStorage: ${customers.length} customers, ${reservations.length} reservations, ${treatments.length} treatments`);
      
      return { customers, reservations, treatments };
    } catch (error) {
      logger.error('❌ Failed to read LocalStorage data:', error);
      throw error;
    }
  }

  /**
   * 顧客データをSupabaseに移行
   */
  private static async migrateCustomersToSupabase(customers: Customer[]): Promise<number> {
    if (!customers.length) return 0;

    try {
      logger.log(`🔄 Migrating ${customers.length} customers to Supabase...`);
      await SupabaseApiService.saveCustomers(customers);
      return customers.length;
    } catch (error) {
      logger.error('❌ Failed to migrate customers:', error);
      throw error;
    }
  }

  /**
   * 予約データをSupabaseに移行
   */
  private static async migrateReservationsToSupabase(reservations: ExtendedReservation[]): Promise<number> {
    if (!reservations.length) return 0;

    try {
      logger.log(`🔄 Migrating ${reservations.length} reservations to Supabase...`);
      await SupabaseApiService.saveReservations(reservations);
      return reservations.length;
    } catch (error) {
      logger.error('❌ Failed to migrate reservations:', error);
      throw error;
    }
  }

  /**
   * 施術記録をSupabaseに移行
   */
  private static async migrateTreatmentsToSupabase(treatments: TreatmentRecord[]): Promise<number> {
    if (!treatments.length) return 0;

    try {
      logger.log(`🔄 Migrating ${treatments.length} treatments to Supabase...`);
      await SupabaseApiService.saveTreatments(treatments);
      return treatments.length;
    } catch (error) {
      logger.error('❌ Failed to migrate treatments:', error);
      throw error;
    }
  }

  /**
   * データ整合性チェック
   * 移行後のデータがLocalStorageのデータと一致するか検証
   */
  private static async verifyMigrationIntegrity(originalData: {
    customers: Customer[];
    reservations: ExtendedReservation[];
    treatments: TreatmentRecord[];
  }): Promise<{
    isValid: boolean;
    errors: string[];
  }> {
    const errors: string[] = [];

    try {
      // Supabaseから移行後のデータを取得
      const [supabaseCustomers, supabaseReservations, supabaseTreatments] = await Promise.all([
        SupabaseApiService.getCustomers(),
        SupabaseApiService.getReservations(),
        SupabaseApiService.getTreatments()
      ]);

      // 件数チェック
      if (originalData.customers.length !== supabaseCustomers.length) {
        errors.push(`Customer count mismatch: LocalStorage(${originalData.customers.length}) vs Supabase(${supabaseCustomers.length})`);
      }

      if (originalData.reservations.length !== supabaseReservations.length) {
        errors.push(`Reservation count mismatch: LocalStorage(${originalData.reservations.length}) vs Supabase(${supabaseReservations.length})`);
      }

      if (originalData.treatments.length !== supabaseTreatments.length) {
        errors.push(`Treatment count mismatch: LocalStorage(${originalData.treatments.length}) vs Supabase(${supabaseTreatments.length})`);
      }

      // 基本データ整合性チェック（顧客コード、メールアドレスなどのユニークフィールド）
      const originalCustomerCodes = new Set(originalData.customers.map(c => c.customerCode));
      const migratedCustomerCodes = new Set(supabaseCustomers.map(c => c.customerCode));
      
      if (originalCustomerCodes.size !== migratedCustomerCodes.size) {
        errors.push(`Customer code uniqueness mismatch`);
      }

      return {
        isValid: errors.length === 0,
        errors
      };

    } catch (error) {
      logger.error('❌ Data integrity check failed:', error);
      errors.push(`Integrity check error: ${(error as Error).message}`);
      return {
        isValid: false,
        errors
      };
    }
  }

  /**
   * バックアップからの復旧
   * 移行失敗時の緊急復旧機能
   */
  private static async restoreFromBackup(backup: BackupData): Promise<void> {
    try {
      logger.log(`🔄 Restoring data from backup (${backup.timestamp})...`);

      // LocalStorageデータの復旧（ブラウザ環境のみ）
      if (typeof window !== 'undefined' && backup.localStorageData) {
        const localStorageService = await import('../utils/localStorage.js');
        
        if (backup.localStorageData.customers.length > 0) {
          await localStorageService.saveCustomers(backup.localStorageData.customers);
        }
        if (backup.localStorageData.reservations.length > 0) {
          await localStorageService.saveReservations(backup.localStorageData.reservations);
        }
        if (backup.localStorageData.treatments.length > 0) {
          await localStorageService.saveTreatments(backup.localStorageData.treatments);
        }
      }

      // Supabaseデータの復旧（利用可能な場合）
      if (getSupabaseAvailability() && backup.supabaseData) {
        if (backup.supabaseData.customers.length > 0) {
          await SupabaseApiService.saveCustomers(backup.supabaseData.customers);
        }
        if (backup.supabaseData.reservations.length > 0) {
          await SupabaseApiService.saveReservations(backup.supabaseData.reservations);
        }
        if (backup.supabaseData.treatments.length > 0) {
          await SupabaseApiService.saveTreatments(backup.supabaseData.treatments);
        }
      }

      logger.log('✅ Data restored from backup successfully');
    } catch (error) {
      logger.error('❌ Failed to restore from backup:', error);
      throw error;
    }
  }

  /**
   * プロバイダー決定ロジック（Supabase/NestJS初期化状態を考慮）
   */
  private static determineProvider(): DataProvider {
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const useNestJSApi = import.meta.env?.VITE_USE_NESTJS_API === 'true';
    const forceLocalStorage = import.meta.env?.VITE_FORCE_LOCALSTORAGE === 'true';
    const hybridMode = import.meta.env?.VITE_HYBRID_MODE === 'true';

    // 強制LocalStorage指定
    if (forceLocalStorage) {
      logger.log('🔒 Forced LocalStorage mode (environment variable)');
      return 'localStorage';
    }

    // NestJS API モード（最優先）
    if (useNestJSApi) {
      logger.log('🚀 NestJS API mode (backend integration enabled)');
      return 'nestjs';
    }

    // Hybrid モード
    if (hybridMode) {
      logger.log('🔀 Hybrid mode enabled');
      return 'hybrid';
    }

    // 動的Supabase初期化状態確認
    let supabaseAvailable = false;

    try {
      // 既にimportされたgetSupabaseAvailability関数を使用
      supabaseAvailable = getSupabaseAvailability();
    } catch (error) {
      logger.warn('⚠️ Failed to check Supabase availability:', error);
      supabaseAvailable = false;
    }

    if (supabaseUrl && !supabaseUrl.includes('your-project-id') && supabaseAvailable) {
      logger.log('☁️ Supabase mode (available and configured)');
      return 'supabase';
    }

    // Supabase設定があるが初期化失敗の場合
    if (supabaseUrl && !supabaseUrl.includes('your-project-id') && !supabaseAvailable) {
      logger.log('⚠️ Supabase configured but unavailable, falling back to localStorage');
      return 'localStorage';
    }

    // デフォルトはLocalStorage
    logger.log('💾 LocalStorage mode (default fallback)');
    return 'localStorage';
  }

  /**
   * 設定更新（動的切り替え用）
   */
  static updateConfig(newConfig: Partial<DataServiceConfig>): void {
    this.config = { ...this.config, ...newConfig };
    logger.log('🔄 DataService config updated:', this.config);
  }

  /**
   * プロバイダー実行（フォールバック対応）
   */
  private static async executeWithFallback<T>(
    entityType: keyof NonNullable<DataServiceConfig['hybridConfig']>,
    operation: string,
    supabaseOperation: () => Promise<T>,
    localStorageOperation: () => T | Promise<T>,
    nestjsOperation?: () => Promise<T>
  ): Promise<T> {
    this.initialize();

    // Hybridモードでのプロバイダー決定
    let provider = this.config.provider;
    if (provider === 'hybrid' && this.config.hybridConfig) {
      provider = this.config.hybridConfig[entityType];
    }

    try {
      if (provider === 'nestjs' && nestjsOperation) {
        logger.log(`🚀 [${entityType}] ${operation} via NestJS API`);
        return await nestjsOperation();
      } else if (provider === 'supabase') {
        logger.log(`☁️ [${entityType}] ${operation} via Supabase`);
        return await supabaseOperation();
      } else {
        logger.log(`💾 [${entityType}] ${operation} via LocalStorage`);
        return await Promise.resolve(localStorageOperation());
      }
    } catch (error) {
      logger.error(`❌ [${entityType}] ${operation} failed with ${provider}:`, error);

      // フォールバック実行
      if (this.config.fallbackProvider === 'localStorage' && (provider === 'supabase' || provider === 'nestjs')) {
        logger.log(`🔄 [${entityType}] Fallback to LocalStorage`);
        try {
          return await Promise.resolve(localStorageOperation());
        } catch (fallbackError) {
          logger.error(`❌ [${entityType}] Fallback also failed:`, fallbackError);
          throw fallbackError;
        }
      }

      throw error;
    }
  }

  // =================================
  // 顧客管理 API
  // =================================

  static async getCustomers(): Promise<Customer[]> {
    return DataService.executeWithFallback(
      'customers',
      'getCustomers',
      () => SupabaseApiService.getCustomers(),
      () => LocalStorageService.getCustomers(),
      () => NestJSApiService.default.getCustomers()
    );
  }

  static async saveCustomers(customers: Customer[]): Promise<void> {
    return DataService.executeWithFallback(
      'customers',
      'saveCustomers',
      () => SupabaseApiService.saveCustomers(customers),
      () => LocalStorageService.saveCustomers(customers)
    );
  }

  static async addCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode' | 'messageSettings' | 'deliveryHistory' | 'mergeHistory' | 'mergeCandidates' | 'customFields'>): Promise<Customer> {
    return DataService.executeWithFallback(
      'customers',
      'addCustomer',
      () => SupabaseApiService.addCustomer(customerData),
      () => LocalStorageService.addCustomer(customerData),
      () => NestJSApiService.default.createCustomer({
        ...customerData,
        messageSettings: { email: true, sms: true, line: false, myPage: false },
        deliveryHistory: [],
        mergeHistory: [],
        mergeCandidates: [],
        customFields: {}
      } as Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode'>)
    );
  }

  static async updateCustomer(id: number, updates: Partial<Customer>): Promise<Customer> {
    return DataService.executeWithFallback(
      'customers',
      'updateCustomer',
      () => SupabaseApiService.updateCustomer(id, updates),
      () => LocalStorageService.updateCustomer(id, updates),
      () => NestJSApiService.default.updateCustomer(id, updates)
    );
  }

  static async deleteCustomer(id: number): Promise<void> {
    return DataService.executeWithFallback(
      'customers',
      'deleteCustomer',
      () => SupabaseApiService.deleteCustomer(id),
      () => LocalStorageService.deleteCustomer(id),
      () => NestJSApiService.default.deleteCustomer(id)
    );
  }

  // =================================
  // 予約管理 API
  // =================================

  static async getReservations(): Promise<ExtendedReservation[]> {
    return DataService.executeWithFallback(
      'reservations',
      'getReservations',
      () => SupabaseApiService.getReservations(),
      () => LocalStorageService.getReservations(),
      () => NestJSApiService.default.getReservations()
    );
  }

  static async saveReservations(reservations: ExtendedReservation[]): Promise<void> {
    return DataService.executeWithFallback(
      'reservations',
      'saveReservations',
      () => SupabaseApiService.saveReservations(reservations),
      () => LocalStorageService.saveReservations(reservations)
    );
  }

  // =================================
  // 施術記録管理 API
  // =================================

  static async getTreatments(): Promise<TreatmentRecord[]> {
    return DataService.executeWithFallback(
      'treatments',
      'getTreatments',
      () => SupabaseApiService.getTreatments(),
      () => LocalStorageService.getTreatments()
    );
  }

  static async saveTreatments(treatments: TreatmentRecord[]): Promise<void> {
    return DataService.executeWithFallback(
      'treatments',
      'saveTreatments',
      () => SupabaseApiService.saveTreatments(treatments),
      () => LocalStorageService.saveTreatments(treatments)
    );
  }

  static async addTreatment(treatmentData: Omit<TreatmentRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<TreatmentRecord> {
    return DataService.executeWithFallback(
      'treatments',
      'addTreatment',
      () => SupabaseApiService.addTreatment(treatmentData),
      () => LocalStorageService.addTreatment(treatmentData)
    );
  }

  static async updateTreatment(id: string, updates: Partial<TreatmentRecord>): Promise<TreatmentRecord> {
    return DataService.executeWithFallback(
      'treatments',
      'updateTreatment',
      () => SupabaseApiService.updateTreatment(parseInt(id, 10), updates),
      () => LocalStorageService.updateTreatment(id, updates)
    );
  }

  // =================================
  // シーケンス管理 API
  // =================================

  static async getSequences(): Promise<Sequences> {
    return DataService.executeWithFallback(
      'sequences',
      'getSequences',
      () => SupabaseApiService.getSequences(),
      () => LocalStorageService.getSequences()
    );
  }

  static async updateSequence(key: keyof Sequences, value: number): Promise<void> {
    return DataService.executeWithFallback(
      'sequences',
      'updateSequence',
      () => SupabaseApiService.updateSequence(key, value),
      () => LocalStorageService.updateSequence(key, value)
    );
  }

  // =================================
  // スタッフシフト管理 API
  // =================================

  static async getStaffShifts(): Promise<StaffShiftData[]> {
    return DataService.executeWithFallback(
      'staffShifts',
      'getStaffShifts',
      () => SupabaseApiService.getStaffShifts(),
      () => LocalStorageService.getStaffShifts()
    );
  }

  static async saveStaffShifts(shifts: StaffShiftData[]): Promise<void> {
    return DataService.executeWithFallback(
      'staffShifts',
      'saveStaffShifts',
      () => SupabaseApiService.saveStaffShifts(shifts),
      () => LocalStorageService.saveStaffShifts(shifts)
    );
  }

  static async addStaffShift(shiftData: Omit<StaffShiftData, 'id' | 'createdAt' | 'updatedAt'>): Promise<StaffShiftData> {
    return DataService.executeWithFallback(
      'staffShifts',
      'addStaffShift',
      () => SupabaseApiService.addStaffShift(shiftData),
      () => LocalStorageService.addStaffShift(shiftData)
    );
  }

  static async updateStaffShift(id: string, updates: Partial<StaffShiftData>): Promise<StaffShiftData> {
    return DataService.executeWithFallback(
      'staffShifts',
      'updateStaffShift',
      () => SupabaseApiService.updateStaffShift(id, updates),
      () => LocalStorageService.updateStaffShift(id, updates)
    );
  }

  static async deleteStaffShift(id: string): Promise<void> {
    return DataService.executeWithFallback(
      'staffShifts',
      'deleteStaffShift',
      () => SupabaseApiService.deleteStaffShift(id),
      () => LocalStorageService.deleteStaffShift(id)
    );
  }

  // 拡張シフトデータ対応
  static async getExtendedStaffShifts(): Promise<ExtendedStaffShiftData[]> {
    return DataService.executeWithFallback(
      'staffShifts',
      'getExtendedStaffShifts',
      () => SupabaseApiService.getExtendedStaffShifts(),
      () => LocalStorageService.getExtendedStaffShifts()
    );
  }

  static async saveExtendedStaffShifts(shifts: ExtendedStaffShiftData[]): Promise<void> {
    return DataService.executeWithFallback(
      'staffShifts',
      'saveExtendedStaffShifts',
      () => SupabaseApiService.saveExtendedStaffShifts(shifts),
      () => LocalStorageService.saveExtendedStaffShifts(shifts)
    );
  }

  // =================================
  // 施術記録 (Treatment Records) API
  // =================================

  /**
   * 指定顧客の全施術記録を取得
   */
  static async getTreatmentRecords(customerId: number): Promise<TreatmentRecord[]> {
    return DataService.executeWithFallback(
      'treatmentRecords',
      'getTreatmentRecords',
      () => SupabaseApiService.getTreatmentRecords(customerId),
      () => LocalStorageService.getTreatmentRecords(customerId)
    );
  }

  /**
   * 指定顧客の最新施術記録を取得
   */
  static async getLatestTreatmentRecord(customerId: number): Promise<TreatmentRecord | null> {
    return DataService.executeWithFallback(
      'treatmentRecords',
      'getLatestTreatmentRecord',
      () => SupabaseApiService.getLatestTreatmentRecord(customerId),
      () => LocalStorageService.getLatestTreatmentRecord(customerId)
    );
  }

  /**
   * 新規施術記録を保存
   */
  static async saveTreatmentRecord(customerId: number, record: TreatmentRecordInput): Promise<TreatmentRecord> {
    return DataService.executeWithFallback(
      'treatmentRecords',
      'saveTreatmentRecord',
      () => SupabaseApiService.saveTreatmentRecord(customerId, record),
      () => LocalStorageService.saveTreatmentRecord(customerId, record)
    );
  }

  /**
   * 既存施術記録を更新
   */
  static async updateTreatmentRecord(customerId: number, id: string, updates: Partial<TreatmentRecord>): Promise<TreatmentRecord | null> {
    return DataService.executeWithFallback(
      'treatmentRecords',
      'updateTreatmentRecord',
      () => SupabaseApiService.updateTreatmentRecord(customerId, id, updates),
      () => LocalStorageService.updateTreatmentRecord(customerId, id, updates)
    );
  }

  /**
   * 指定期間の施術記録を取得
   */
  static async getTreatmentRecordsByDateRange(customerId: number, startDate: Date, endDate: Date): Promise<TreatmentRecord[]> {
    return DataService.executeWithFallback(
      'treatmentRecords',
      'getTreatmentRecordsByDateRange',
      () => SupabaseApiService.getTreatmentRecordsByDateRange(customerId, startDate, endDate),
      () => LocalStorageService.getTreatmentRecordsByDateRange(customerId, startDate, endDate)
    );
  }

  /**
   * 指定顧客の来店回数を取得
   */
  static async getVisitCount(customerId: number): Promise<number> {
    return DataService.executeWithFallback(
      'treatmentRecords',
      'getVisitCount',
      () => SupabaseApiService.getVisitCount(customerId),
      () => LocalStorageService.getVisitCount(customerId)
    );
  }

  /**
   * 施術記録を削除
   */
  static async deleteTreatmentRecord(customerId: number, id: string): Promise<boolean> {
    return DataService.executeWithFallback(
      'treatmentRecords',
      'deleteTreatmentRecord',
      () => SupabaseApiService.deleteTreatmentRecord(customerId, id),
      () => LocalStorageService.deleteTreatmentRecord(customerId, id)
    );
  }

  // =================================
  // ユーティリティ関数
  // =================================

  static async clearAllData(): Promise<void> {
    this.initialize();
    
    if (this.config.provider === 'supabase' || this.config.provider === 'hybrid') {
      await SupabaseApiService.clearAllData();
    }
    
    if (this.config.provider === 'localStorage' || this.config.provider === 'hybrid') {
      LocalStorageService.clearAllData();
    }
  }

  static async initializeWithMockData(mockCustomers: Partial<Customer>[]): Promise<void> {
    return DataService.executeWithFallback(
      'customers',
      'initializeWithMockData',
      () => SupabaseApiService.initializeWithMockData(mockCustomers),
      () => LocalStorageService.initializeWithMockData(mockCustomers)
    );
  }

  // =================================
  // 監視・診断機能
  // =================================

  /**
   * 現在の設定状況を取得
   */
  static getServiceStatus(): {
    provider: DataProvider;
    config: DataServiceConfig;
    isSupabaseAvailable: boolean;
    environment: string;
  } {
    this.initialize();
    
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const isSupabaseAvailable = Boolean(
      supabaseUrl && !supabaseUrl.includes('your-project-id')
    );

    return {
      provider: this.config.provider,
      config: this.config,
      isSupabaseAvailable,
      environment: import.meta.env?.VITE_APP_ENV || 'development'
    };
  }

  /**
   * データプロバイダーのヘルスチェック
   */
  static async healthCheck(): Promise<{
    localStorage: boolean;
    supabase: boolean;
    activeProvider: DataProvider;
    recommendations: string[];
    environment: 'browser' | 'nodejs';
  }> {
    const recommendations: string[] = [];
    const environment = typeof window !== 'undefined' ? 'browser' : 'nodejs';
    
    // LocalStorage チェック（環境を考慮）
    let localStorageHealthy = false;
    try {
      if (typeof localStorage !== 'undefined') {
        const testKey = 'salonhub_healthcheck';
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        localStorageHealthy = true;
      } else if (environment === 'nodejs') {
        // Node.js環境ではLocalStorageは利用不可能だが、これは正常な状態
        localStorageHealthy = false;
        recommendations.push('LocalStorage not available in Node.js environment (expected)');
      }
    } catch (error) {
      recommendations.push(`LocalStorage is not available: ${(error as Error).message}`);
    }

    // Supabase チェック
    let supabaseHealthy = false;
    try {
      await SupabaseApiService.getCustomers();
      supabaseHealthy = true;
    } catch (error) {
      recommendations.push(`Supabase connection failed: ${(error as Error).message}`);
    }

    const status = this.getServiceStatus();

    // 推奨事項
    if (!supabaseHealthy && status.provider === 'supabase') {
      recommendations.push('Switch to LocalStorage as fallback');
    }

    if (localStorageHealthy && supabaseHealthy) {
      recommendations.push('Both providers are healthy - consider gradual migration');
    }

    if (environment === 'nodejs' && supabaseHealthy) {
      recommendations.push('Supabase is the optimal choice for Node.js environment');
    }

    return {
      localStorage: localStorageHealthy,
      supabase: supabaseHealthy,
      activeProvider: status.provider,
      recommendations,
      environment
    };
  }

  /**
   * データ同期状況確認
   */
  // =================================
  // Settings管理 API (Issue #238)
  // =================================

  /**
   * 設定データを取得
   */
  static async getSettings(): Promise<SettingsResponse> {
    return DataService.executeWithFallback(
      'settings',
      'getSettings',
      () => SupabaseApiService.getSettings(),
      () => {
        // LocalStorageフォールバック
        const stored = localStorage.getItem('salonhub-settings');
        return {
          success: true,
          data: stored ? JSON.parse(stored) : null,
          error: null
        };
      }
    );
  }

  /**
   * 設定データを保存
   */
  static async saveSettings(settings: SettingsData): Promise<SettingsResponse> {
    return DataService.executeWithFallback(
      'settings',
      'saveSettings',
      () => SupabaseApiService.saveSettings(settings),
      () => {
        // LocalStorageフォールバック
        localStorage.setItem('salonhub-settings', JSON.stringify(settings));
        return {
          success: true,
          data: settings,
          error: null
        };
      }
    );
  }

  /**
   * 特定の設定セクションを更新
   */
  static async updateSettingsSection(section: string, updates: Record<string, unknown>): Promise<SettingsResponse> {
    return DataService.executeWithFallback(
      'settings',
      'updateSettingsSection',
      () => SupabaseApiService.updateSettingsSection(section, updates),
      () => {
        // LocalStorageフォールバック
        const stored = localStorage.getItem('salonhub-settings');
        const currentSettings = stored ? JSON.parse(stored) : {};
        const updatedSettings = {
          ...currentSettings,
          [section]: { ...currentSettings[section], ...updates },
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem('salonhub-settings', JSON.stringify(updatedSettings));
        
        return {
          success: true,
          data: updatedSettings,
          error: null
        };
      }
    );
  }

  /**
   * 設定データをエクスポート
   */
  static async exportSettings(): Promise<SettingsResponse> {
    return DataService.executeWithFallback(
      'settings',
      'exportSettings',
      () => SupabaseApiService.exportSettings(),
      () => {
        // LocalStorageフォールバック
        const stored = localStorage.getItem('salonhub-settings');
        const settings = stored ? JSON.parse(stored) : null;
        
        return {
          success: true,
          data: {
            settings,
            exportedAt: new Date().toISOString(),
            version: '1.0.0'
          },
          error: null
        };
      }
    );
  }

  /**
   * 設定データをバックアップ
   */
  static async backupSettings(): Promise<SettingsBackup> {
    try {
      const settingsResponse = await DataService.getSettings();
      const backup: SettingsBackup = {
        settings: settingsResponse.data || {},
        timestamp: new Date().toISOString(),
        version: '1.0.0'
      };
      
      // 複数の場所にバックアップを保存
      const promises = [];
      
      // LocalStorageバックアップ
      if (typeof localStorage !== 'undefined') {
        promises.push(
          Promise.resolve().then(() => {
            localStorage.setItem('salonhub-settings-backup', JSON.stringify(backup));
          })
        );
      }
      
      // Supabase利用可能な場合はSupabaseにもバックアップ
      if (getSupabaseAvailability()) {
        promises.push(SupabaseApiService.backupSettings(backup as unknown as Record<string, unknown>).catch(error => {
          logger.warn('⚠️ Supabase backup failed:', error);
        }));
      }
      
      await Promise.allSettled(promises);
      logger.log('✅ Settings backup created successfully');
      return backup;
    } catch (error) {
      logger.error('❌ Failed to create settings backup:', error);
      throw error;
    }
  }

  /**
   * 設定データを復元
   */
  static async restoreSettings(backup?: SettingsBackup): Promise<void> {
    try {
      let settingsToRestore = backup;
      
      if (!settingsToRestore) {
        // バックアップが指定されていない場合、最新のバックアップを取得
        const stored = localStorage.getItem('salonhub-settings-backup');
        if (stored) {
          settingsToRestore = JSON.parse(stored);
        } else if (getSupabaseAvailability()) {
          const supabaseResult = await SupabaseApiService.getLatestSettingsBackup();
          if (supabaseResult && supabaseResult.data) {
            settingsToRestore = supabaseResult.data as unknown as SettingsBackup;
          }
        }
      }
      
      if (!settingsToRestore || !settingsToRestore.settings) {
        throw new Error('No backup data found to restore');
      }
      
      await DataService.saveSettings(settingsToRestore.settings);
      logger.log(`✅ Settings restored from backup (${settingsToRestore.timestamp})`);
    } catch (error) {
      logger.error('❌ Failed to restore settings from backup:', error);
      throw error;
    }
  }

  /**
   * 設定データの整合性チェック
   */
  static async validateSettingsIntegrity(settings: SettingsData): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  }> {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    try {
      // 基本構造チェック
      if (!settings || typeof settings !== 'object') {
        errors.push('Settings data is not a valid object');
        return { isValid: false, errors, warnings };
      }
      
      // 必須セクションの存在チェック
      const requiredSections = ['store', 'users', 'data', 'notifications', 'ai', 'display', 'system'];
      const missingSections = requiredSections.filter(section => !settings[section]);
      
      if (missingSections.length > 0) {
        errors.push(`Missing required sections: ${missingSections.join(', ')}`);
      }
      
      // 店舗設定の詳細チェック
      if (settings.store && typeof settings.store === 'object') {
        const store = settings.store as StoreSettings;
        if (!store.storeName || (typeof store.storeName === 'string' && store.storeName.length === 0)) {
          errors.push('Store name is required');
        }
        if (store.storeName && typeof store.storeName === 'string' && store.storeName.length > 100) {
          warnings.push('Store name is very long (>100 characters)');
        }
        if (store.taxRate && typeof store.taxRate === 'number' && (store.taxRate < 0 || store.taxRate > 1)) {
          errors.push('Tax rate must be between 0 and 1');
        }
      }
      
      // ユーザー設定の詳細チェック
      if (settings.users && typeof settings.users === 'object') {
        const users = settings.users as UserSettings;
        if (users.passwordPolicy && typeof users.passwordPolicy === 'object') {
          const policy = users.passwordPolicy as PasswordPolicySettings;
          if (policy.minLength && typeof policy.minLength === 'number' && policy.minLength < 6) {
            warnings.push('Password minimum length is less than 6 characters');
          }
          if (policy.expirationDays && typeof policy.expirationDays === 'number' && policy.expirationDays < 30) {
            warnings.push('Password expiration is less than 30 days');
          }
        }
      }
      
      // データ管理設定チェック
      if (settings.data && typeof settings.data === 'object') {
        const data = settings.data as DataSettings;
        if (data.customerData && typeof data.customerData === 'object') {
          const customerData = data.customerData;
          if (customerData.retentionDays && typeof customerData.retentionDays === 'number' && customerData.retentionDays < 365) {
            warnings.push('Customer data retention is less than 1 year');
          }
        }
      }

      // AI設定チェック
      if (settings.ai && typeof settings.ai === 'object') {
        const ai = settings.ai as Record<string, unknown>;
        if (ai.monthlyQueryLimit && typeof ai.monthlyQueryLimit === 'number' && ai.monthlyQueryLimit < 100) {
          warnings.push('AI monthly query limit is very low (<100)');
        }
      }
      
      // システム設定チェック
      if (settings.system && typeof settings.system === 'object') {
        const system = settings.system as Record<string, unknown>;
        if (system.maxConcurrentUsers && typeof system.maxConcurrentUsers === 'number' && system.maxConcurrentUsers < 10) {
          warnings.push('Max concurrent users limit is very low (<10)');
        }
      }
      
      const isValid = errors.length === 0;
      
      logger.log(`⚙️ Settings integrity check: ${isValid ? 'PASSED' : 'FAILED'}`);
      if (errors.length > 0) {
        logger.warn('❌ Validation errors:', errors);
      }
      if (warnings.length > 0) {
        logger.warn('⚠️ Validation warnings:', warnings);
      }
      
      return { isValid, errors, warnings };
    } catch (error) {
      errors.push(`Validation process failed: ${(error as Error).message}`);
      return { isValid: false, errors, warnings };
    }
  }

  /**
   * 設定の変更ログを記録
   */
  static async logSettingsChange(change: {
    userId: string;
    userName: string;
    section: string;
    field: string;
    oldValue?: unknown;
    newValue: unknown;
    timestamp?: Date;
  }): Promise<void> {
    try {
      const logEntry = {
        id: Date.now().toString(),
        ...change,
        timestamp: change.timestamp || new Date(),
        ipAddress: 'localhost', // 将来的にはリアルIPを取得
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server'
      };
      
      // 変更ログをLocalStorageに保存（最新100件のみ保持）
      const logKey = 'salonhub-settings-changelog';
      const existingLogs = localStorage.getItem(logKey);
      const logs = existingLogs ? JSON.parse(existingLogs) : [];
      
      logs.unshift(logEntry);
      const trimmedLogs = logs.slice(0, 100); // 最新100件のみ保持
      
      localStorage.setItem(logKey, JSON.stringify(trimmedLogs));
      
      // Supabase利用可能な場合は永続化
      if (getSupabaseAvailability()) {
        await SupabaseApiService.logSettingsChange(logEntry).catch(error => {
          logger.warn('⚠️ Failed to log to Supabase:', error);
        });
      }
      
      logger.log(`📝 Settings change logged: ${change.section}.${change.field}`);
    } catch (error) {
      logger.error('❌ Failed to log settings change:', error);
      // ログ記録の失敗は設定変更自体を阻害しないよう、エラーをthrowしない
    }
  }

  static async syncStatus(): Promise<{
    customerCount: { localStorage: number; supabase: number };
    reservationCount: { localStorage: number; supabase: number };
    treatmentCount: { localStorage: number; supabase: number };
    inSync: boolean;
    environment: 'browser' | 'nodejs';
  }> {
    const environment = typeof window !== 'undefined' ? 'browser' : 'nodejs';
    
    try {
      // LocalStorageは環境に応じて処理
      let localCustomers: Customer[] = [];
      let localReservations: ExtendedReservation[] = [];
      let localTreatments: TreatmentRecord[] = [];

      if (environment === 'browser') {
        // ブラウザ環境でのみLocalStorageアクセス
        try {
          localCustomers = LocalStorageService.getCustomers();
        } catch {
          localCustomers = [];
        }
        
        try {
          localReservations = LocalStorageService.getReservations();
        } catch {
          localReservations = [];
        }
        
        try {
          localTreatments = LocalStorageService.getTreatments();
        } catch {
          localTreatments = [];
        }
      }

      // Supabaseアクセス（環境に関係なく）
      const [supabaseCustomers, supabaseReservations, supabaseTreatments] = await Promise.all([
        SupabaseApiService.getCustomers().catch(() => []),
        SupabaseApiService.getReservations().catch(() => []),
        SupabaseApiService.getTreatments().catch(() => [])
      ]);

      const customerCount = {
        localStorage: localCustomers.length,
        supabase: supabaseCustomers.length
      };

      const reservationCount = {
        localStorage: localReservations.length,
        supabase: supabaseReservations.length
      };

      const treatmentCount = {
        localStorage: localTreatments.length,
        supabase: supabaseTreatments.length
      };

      const inSync = (
        customerCount.localStorage === customerCount.supabase &&
        reservationCount.localStorage === reservationCount.supabase &&
        treatmentCount.localStorage === treatmentCount.supabase
      );

      return {
        customerCount,
        reservationCount,
        treatmentCount,
        inSync,
        environment
      };
    } catch (error) {
      logger.error('❌ Failed to check sync status:', error);
      throw error;
    }
  }
}

// 自動初期化
DataService.initialize();

// デフォルトエクスポート
export default DataService;

// 名前付きエクスポート（既存コードとの互換性）
export const {
  getCustomers,
  saveCustomers,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  getReservations,
  saveReservations,
  getTreatments,
  saveTreatments,
  addTreatment,
  updateTreatment,
  getSequences,
  updateSequence,
  clearAllData,
  initializeWithMockData
} = DataService;